import type { FormEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { api, type Item, type MusicAlbumDetail, type MusicAlbumPatch } from '../api.ts';
import { AlbumCard, Cover, formatDuration } from '../components/MusicCards.tsx';
import { ErrorNotice, Loading } from '../components/States.tsx';
import { fileToBase64 } from '../fileBase64.ts';
import { ArrowLeftIcon, PlayIcon, PlusIcon, SparklesIcon, TrashIcon } from '../Icon.tsx';
import { useSession } from '../session.tsx';

const MAX_COVER_BYTES = 8 * 1024 * 1024;

export function MusicAlbumScreen({
  albumId,
  onBack,
  onPlayTracks,
  onEnqueue,
}: {
  albumId: string;
  onBack: () => void;
  onPlayTracks: (tracks: Item[]) => void;
  onEnqueue: (item: Item) => void;
}): ReactNode {
  const [detail, setDetail] = useState<MusicAlbumDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverMessage, setCoverMessage] = useState<string | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editMessage, setEditMessage] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formArtist, setFormArtist] = useState('');
  const [formYear, setFormYear] = useState('');
  const [formGenres, setFormGenres] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const { user } = useSession();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    let cancelled = false;
    void api
      .musicAlbum(albumId)
      .then((result) => {
        if (!cancelled) setDetail(result);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(caught instanceof Error ? caught.message : 'Albüm okunamadı.');
      });
    return () => {
      cancelled = true;
    };
  }, [albumId]);

  useEffect(() => {
    const album = detail?.album;
    if (album === undefined) return;
    setFormTitle(album.title);
    setFormArtist(album.artist);
    setFormYear(album.year === null ? '' : String(album.year));
    setFormGenres((album.genres ?? []).join(', '));
    setFormNotes(album.notes ?? '');
  }, [detail?.album]);

  if (error !== null) return <ErrorNotice message={error} onRetry={onBack} />;
  if (detail === null) return <Loading label="Albüm yükleniyor…" />;
  const { album, tracks } = detail;

  const uploadCover = async (file: File | undefined): Promise<void> => {
    if (file === undefined || album.coverItemId === null || coverBusy) return;
    setCoverMessage(null);
    if (!file.type.startsWith('image/')) {
      setCoverMessage('Yalnız görsel dosyası seçin.');
      return;
    }
    if (file.size > MAX_COVER_BYTES) {
      setCoverMessage('Kapak en fazla 8 MB olabilir.');
      return;
    }
    setCoverBusy(true);
    try {
      const data = await fileToBase64(file);
      const result = await api.musicUploadCover(album.id, data);
      setDetail((current) => (current === null ? current : { ...current, album: result.album }));
      setCoverMessage('Albüm kapağı güncellendi.');
    } catch (caught) {
      setCoverMessage(caught instanceof Error ? caught.message : 'Kapak yüklenemedi.');
    } finally {
      setCoverBusy(false);
    }
  };

  const removeCover = async (): Promise<void> => {
    if (coverBusy) return;
    setCoverBusy(true);
    setCoverMessage(null);
    try {
      const result = await api.musicDeleteCover(album.id);
      setDetail((current) => (current === null ? current : { ...current, album: result.album }));
      setCoverMessage(
        result.album.coverSource === 'sidecar'
          ? 'Yükleme kaldırıldı; klasördeki kapak kullanılıyor.'
          : 'Yüklenen kapak kaldırıldı.',
      );
    } catch (caught) {
      setCoverMessage(caught instanceof Error ? caught.message : 'Kapak kaldırılamadı.');
    } finally {
      setCoverBusy(false);
    }
  };

  const saveAlbum = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (editBusy) return;
    setEditBusy(true);
    setEditMessage(null);
    const year = Number.parseInt(formYear, 10);
    const patch: MusicAlbumPatch = {
      title: formTitle.trim() === '' ? null : formTitle.trim(),
      artist: formArtist.trim() === '' ? null : formArtist.trim(),
      year: Number.isFinite(year) ? year : null,
      genres: splitList(formGenres),
      notes: formNotes.trim() === '' ? null : formNotes.trim(),
    };
    try {
      const result = await api.musicUpdateAlbum(album.id, patch);
      setDetail((current) => (current === null ? current : { ...current, album: result.album }));
      setEditMessage('Albüm bilgileri kaydedildi.');
    } catch (caught) {
      setEditMessage(caught instanceof Error ? caught.message : 'Albüm bilgileri kaydedilemedi.');
    } finally {
      setEditBusy(false);
    }
  };

  const resetAlbum = async (): Promise<void> => {
    if (editBusy) return;
    setEditBusy(true);
    setEditMessage(null);
    try {
      const result = await api.musicResetAlbum(album.id);
      setDetail((current) => (current === null ? current : { ...current, album: result.album }));
      setEditMessage('Albüm bilgileri parça etiketlerine döndürüldü.');
    } catch (caught) {
      setEditMessage(caught instanceof Error ? caught.message : 'Sıfırlanamadı.');
    } finally {
      setEditBusy(false);
    }
  };

  return (
    <div className="music-detail">
      <button type="button" className="button button--ghost music-detail__back" onClick={onBack}>
        <ArrowLeftIcon size={15} /> Müzik
      </button>

      <header className="music-detail__header">
        <div className="music-detail__cover">
          <Cover
            itemId={album.coverItemId}
            thumbnailUrl={album.coverThumbnailUrl}
            alt={`${album.title} kapağı`}
          />
        </div>
        <div className="music-detail__meta">
          <span className="music-detail__eyebrow">Albüm</span>
          <h1>{album.title}</h1>
          <p>{album.artist}</p>
          <small>
            {album.year === null ? '' : `${album.year} · `}
            {album.trackCount} parça
            {album.durationMs > 0 ? ` · ${formatDuration(album.durationMs)}` : ''}
            {album.coverSource === 'sidecar' ? ' · Kapak klasörden' : ''}
            {album.coverSource === 'upload' ? ' · Kapak yüklendi' : ''}
          </small>
          {(album.genres?.length ?? 0) > 0 && (
            <div className="music-detail__genres">
              {(album.genres ?? []).map((genre) => (
                <span className="badge" key={genre}>
                  {genre}
                </span>
              ))}
            </div>
          )}
          {album.notes != null && <p className="music-detail__notes">{album.notes}</p>}
          <div className="music-detail__actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => onPlayTracks(tracks)}
              disabled={tracks.length === 0}
            >
              <PlayIcon size={15} /> Albümü çal
            </button>
            <button
              type="button"
              className="button button--ghost"
              onClick={() => {
                for (const track of tracks) onEnqueue(track);
              }}
              disabled={tracks.length === 0}
            >
              <PlusIcon size={15} /> Sıraya ekle
            </button>
            {isAdmin && (
              <label className="button button--ghost music-detail__cover-upload">
                {coverBusy ? 'Yükleniyor…' : 'Kapak değiştir'}
                <input
                  type="file"
                  className="metadata-artwork__input"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={coverBusy}
                  onChange={(event) => {
                    void uploadCover(event.currentTarget.files?.[0]);
                    event.currentTarget.value = '';
                  }}
                />
              </label>
            )}
            {isAdmin && album.coverSource === 'upload' && (
              <button
                type="button"
                className="button button--ghost music-detail__cover-remove"
                disabled={coverBusy}
                onClick={() => void removeCover()}
              >
                <TrashIcon size={14} /> Kapağı kaldır
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                className="button button--ghost"
                aria-expanded={showEdit}
                onClick={() => setShowEdit((value) => !value)}
              >
                <SparklesIcon size={14} />
                {showEdit ? 'Düzenlemeyi kapat' : 'Albüm bilgilerini düzenle'}
              </button>
            )}
          </div>
          {coverMessage !== null && (
            <p className="music-detail__cover-message" role="status">
              {coverMessage}
            </p>
          )}
          {isAdmin && (
            <small className="music-detail__cover-hint">
              Klasöre <code>cover.jpg</code> / <code>folder.jpg</code> koyabilir veya buradan
              yükleyebilirsiniz.
            </small>
          )}
        </div>
      </header>

      {isAdmin && showEdit && (
        <section className="music-album-edit" aria-label="Albüm bilgileri">
          <form className="metadata-edit" onSubmit={(event) => void saveAlbum(event)}>
            <div className="metadata-edit__grid">
              <label className="metadata-edit__field">
                <span>Albüm adı</span>
                <input
                  value={formTitle}
                  onChange={(event) => setFormTitle(event.target.value)}
                  maxLength={300}
                />
              </label>
              <label className="metadata-edit__field">
                <span>Sanatçı</span>
                <input
                  value={formArtist}
                  onChange={(event) => setFormArtist(event.target.value)}
                  maxLength={300}
                />
              </label>
              <label className="metadata-edit__field metadata-edit__field--short">
                <span>Yıl</span>
                <input
                  value={formYear}
                  onChange={(event) => setFormYear(event.target.value)}
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="2024"
                />
              </label>
              <label className="metadata-edit__field metadata-edit__field--wide">
                <span>Türler (virgülle)</span>
                <input
                  value={formGenres}
                  onChange={(event) => setFormGenres(event.target.value)}
                  placeholder="Rock, Alternatif"
                />
              </label>
              <label className="metadata-edit__field metadata-edit__field--wide">
                <span>Notlar</span>
                <textarea
                  value={formNotes}
                  onChange={(event) => setFormNotes(event.target.value)}
                  rows={3}
                  maxLength={5000}
                />
              </label>
            </div>
            <div className="metadata-panel__actions">
              <button type="submit" className="button button--primary" disabled={editBusy}>
                {editBusy ? 'Kaydediliyor…' : 'Albümü kaydet'}
              </button>
              <button
                type="button"
                className="button button--ghost metadata-panel__clear"
                disabled={editBusy}
                onClick={() => void resetAlbum()}
              >
                <TrashIcon size={14} /> Etiketlere dön
              </button>
            </div>
            {editMessage !== null && (
              <p className="music-detail__cover-message" role="status">
                {editMessage}
              </p>
            )}
            <p className="music-detail__cover-hint">
              Bu bilgiler albümün tamamına uygulanır; parçaların tek tek etiketlerini değiştirmez.
            </p>
          </form>
        </section>
      )}

      <ol className="music-track-list" aria-label="Albüm parçaları">
        {tracks.map((track, index) => (
          <li key={track.id}>
            <button
              type="button"
              className="music-track"
              onClick={() => onPlayTracks(tracks.slice(index))}
            >
              <span className="music-track__number">{track.music?.trackNumber ?? index + 1}</span>
              <span className="music-track__title">{track.title}</span>
              <span className="music-track__duration">
                {track.durationMs === null
                  ? ''
                  : `${Math.floor(track.durationMs / 60000)}:${String(
                      Math.floor((track.durationMs % 60000) / 1000),
                    ).padStart(2, '0')}`}
              </span>
              <PlayIcon size={14} />
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

export function MusicArtistScreen({
  artistId,
  onBack,
  onOpenAlbum,
}: {
  artistId: string;
  onBack: () => void;
  onOpenAlbum: (albumId: string) => void;
}): ReactNode {
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof api.musicArtist>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { user } = useSession();

  useEffect(() => {
    let cancelled = false;
    void api
      .musicArtist(artistId)
      .then((result) => {
        if (!cancelled) setDetail(result);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(caught instanceof Error ? caught.message : 'Sanatçı okunamadı.');
      });
    return () => {
      cancelled = true;
    };
  }, [artistId]);

  if (error !== null) return <ErrorNotice message={error} onRetry={onBack} />;
  if (detail === null) return <Loading label="Sanatçı yükleniyor…" />;

  return (
    <div className="music-detail">
      <button type="button" className="button button--ghost music-detail__back" onClick={onBack}>
        <ArrowLeftIcon size={15} /> Müzik
      </button>
      <header className="music-detail__header music-detail__header--artist">
        <div className="music-detail__cover">
          <Cover
            itemId={detail.artist.coverItemId}
            thumbnailUrl={detail.artist.coverThumbnailUrl}
            alt={`${detail.artist.name} görseli`}
          />
        </div>
        <div className="music-detail__meta">
          <span className="music-detail__eyebrow">Sanatçı</span>
          <h1>{detail.artist.name}</h1>
          <small>
            {detail.artist.albumCount} albüm · {detail.artist.trackCount} parça
          </small>
        </div>
      </header>
      {user?.role === 'admin' && (
        <p className="music-detail__cover-hint">
          Albüm kapağı ve künyesini düzenlemek için aşağıdaki albümlerden birine dokunun.
        </p>
      )}
      <section className="music-section" aria-labelledby="artist-albums-title">
        <div className="section__header">
          <h2 id="artist-albums-title" className="section__title">
            Albümler
          </h2>
        </div>
        <div className="music-album-grid">
          {detail.albums.map((album) => (
            <AlbumCard key={album.id} album={album} onOpen={() => onOpenAlbum(album.id)} />
          ))}
        </div>
      </section>
    </div>
  );
}
